import React, { useCallback, useMemo } from "react";
import { Entity } from "@common/types/entity";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { DisabledWithTooltip } from "@design-system/DisabledWithTooltip/DisabledWithTooltip";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { EntityAvatar } from "@components/avatar/EntityAvatar";

interface Props {
    entities: Entity[];
    selectedEntity?: Entity;
    onChange: (entity: Entity | undefined) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    readOnly?: boolean;
    disabledReason?: string;
    triggerButtonProps?: TriggerButtonProps;
    hideArrow?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export const EntitySelect: React.FC<Props> = ({
    entities,
    selectedEntity,
    onChange,
    placeholder = "Select entity",
    disabled,
    className,
    readOnly,
    disabledReason,
    triggerButtonProps,
    hideArrow,
    onOpenChange,
}) => {
    const isDisabled = disabled || readOnly || !!disabledReason;

    const entitiesById = useMemo(
        () => new Map(entities.map((entity) => [entity.id, entity])),
        [entities],
    );
    const items = useMemo(
        () =>
            entities.map((entity) => ({
                value: entity.id,
                label: entity.name ?? "",
                leading: <EntityAvatar entity={entity} size="xs" aria-hidden />,
            })),
        [entities],
    );

    const onValueChange = useCallback(
        (entityId: number | null) => {
            onChange(
                entityId === null ? undefined : entitiesById.get(entityId),
            );
        },
        [entitiesById, onChange],
    );

    return (
        <DisabledWithTooltip reason={disabledReason}>
            <Picker.Select
                items={items}
                value={selectedEntity?.id ?? null}
                onValueChange={onValueChange}
                onOpenChange={onOpenChange}
                disabled={isDisabled}
                placeholder={placeholder}
                chevron={hideArrow ? "none" : undefined}
                renderValue={() =>
                    selectedEntity ? (
                        <EntityDisplay entity={selectedEntity} />
                    ) : undefined
                }
                trigger={
                    <Picker.Button
                        variant="primary"
                        className={className}
                        {...triggerButtonProps}
                        size={triggerButtonProps?.size ?? "md"}
                    />
                }
            />
        </DisabledWithTooltip>
    );
};
